import React from "react";
import { I18n } from "i18n-js";
import * as locales from "../../../locales.json";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";
import { ResponsiveBar } from "@nivo/bar";
import MinigraphCard from "./minigraphCard";
import AnalyticsLoading from "../analyticsLoading";
import AnalyticsError from "../analyticsError";
import _ from "underscore";

interface LeaderboardCardProps {
  devMode: boolean;
  colors: string[];
  data: any;
  fetchLeaderboardData: any;
  teamId: string;
  requestStatus: string;
}

export default class LeaderboardCard extends React.Component<
  LeaderboardCardProps,
  any
> {
  componentDidMount() {
    if (this.props.requestStatus === "loading") {
      this.props.fetchLeaderboardData(this.props.teamId);
    }
  }

  render() {
    return (
      <MinigraphCard
        devMode={this.props.devMode}
        title={this.getTitle()}
        url={this.getUrl()}
        chartName="leaderboard"
      >
        {this.renderCardContent()}
      </MinigraphCard>
    );
  }

  renderCardContent = () => {
    if (this.props.requestStatus === "error") {
      return (
        <AnalyticsError
          error={i18n.translate("analyticsv2.something_went_wrong")}
        />
      );
    }
    if (this.props.requestStatus === "loading") {
      return <AnalyticsLoading />;
    }
    if (_.isEmpty(this.props.data)) {
      return (
        <AnalyticsError
          error={i18n.translate("analyticsv2.leaderboard_card.not_enough_data")}
        />
      );
    }
    if (this.props.requestStatus === "success") {
      return this.renderLeaderboardGraph();
    }

    return null;
  };

  renderLeaderboardGraph = () => {
    return (
      <div style={{ height: this.getHeight() }}>
        <ResponsiveBar
          data={this.getChartData(this.props.data)}
          keys={["value"]}
          indexBy="user_id"
          colors={this.props.colors}
          colorBy="index"
          margin={{
            top: -10,
            right: 35,
            bottom: 70,
            left: 140,
          }}
          padding={0.4}
          layout="horizontal"
          borderRadius={3}
          axisBottom={{
            tickPadding: 0,
            tickSize: 0,
            legend: i18n.translate(
              "analyticsv2.leaderboard_card.bonuses_received_last_period_days",
              { period: 30 }
            ),
            legendPosition: "middle",
            legendOffset: 40,
            tickValues: this.getChartValues(),
          }}
          axisLeft={{
            renderTick: this.customTick,
          }}
          gridXValues={this.getChartValues()}
          enableGridX
          enableGridY={false}
          enableLabel={false}
          animate
          tooltip={({ value }) => {
            return (
              <div style={{ color: "#fefefe", fontWeight: "bold" }}>
                {value}
                {` ${i18n.translate(
                  "analyticsv2.leaderboard_card.bonuses_received"
                )}`}
              </div>
            );
          }}
          theme={{
            fontFamily: [
              "Open Sans",
              "Arial",
              "Helvetica Neue",
              "Helvetica",
              "sans-serif",
            ],
            fontSize: 14,
            axis: {
              legend: {
                float: "left",
                text: {
                  fontSize: 14,
                },
              },
            },
            grid: {
              line: {
                stroke: "#e7ebed",
                strokeWidth: 1,
              },
            },
            tooltip: {
              container: {
                background: "#333",
                opacity: 0.85,
                padding: 8,
              },
            },
          }}
        />
      </div>
    );
  };

  customTick = (tick) => {
    return (
      <g transform={`translate(${tick.x - 90},${tick.y})`}>
        <a href={this.getUser(tick.value).profile_page_path}>
          <g transform="translate(-37,-15)">
            <defs>
              <clipPath id={`avatar-clip-${tick.value}`}>
                <circle cx="16" cy="16" r="16" />
              </clipPath>
            </defs>
            <g>
              <image
                href={this.getUser(tick.value).profile_pic_url}
                width="32"
                height="32"
                clipPath={`url(#avatar-clip-${tick.value})`}
              />
            </g>
          </g>
          <text
            textAnchor="left"
            dominantBaseline="middle"
            style={{
              fill: "#3384CC",
              fontSize: 13,
              fontWeight: "bold",
            }}
          >
            {this.getUser(tick.value).display_name}
          </text>
        </a>
      </g>
    );
  };

  getUser = (userId) => {
    return _.find(this.getChartData(this.props.data), (user) => {
      return user.user_id === userId;
    });
  };

  getTitle = () => {
    return "Leaderboard";
  };

  getUrl = () => {
    return `/analytics/leaderboards?team=${this.props.teamId}`;
  };

  getChartData = (leaderboardData) => {
    const transformedData = leaderboardData.map((user) => {
      return this.getTransformedUser(user);
    });
    return transformedData;
  };

  getTransformedUser = (user) => {
    if (user.display_name.length > 10) {
      return {
        ...user,
        display_name: this.truncateName(user.display_name),
      };
    }
    return user;
  };

  getChartValues = () => {
    if (_.isEmpty(this.props.data)) {
      return undefined;
    }

    const largestValue = _.chain(this.props.data)
      .map((item) => {
        return item.value;
      })
      .max()
      .value();

    if (largestValue <= 7) {
      // Special case to override Nivo's decimal values.
      return [0, 1, 2, 3, 4, 5, 6, 7];
    }
    return undefined;
  };

  truncateName = (name) => {
    return name.slice(0, 9).concat("..");
  };

  getHeight = () => {
    const baseHeight = 150;
    return baseHeight + this.props.data.length * 60;
  };
}
